import React, { useEffect, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import {
  Button,
  Dialog,
  Typography,
  IconButton,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import photoPlaceholder from 'assets/images/user-photo-placeholder.svg';
import PageLoader from 'components/PageLoader2';
import AlertModal from 'components/AlertModal';
import styles from './styles';
import OverlappingModal from 'components/OverlapsingModal';
import { getOptions } from 'helpers/getOptions';
import $api from 'http/index';
import currentWorklogActions from 'store/currentWorklog/actions';
import { updateSheets } from 'store/schedule/schedulePageOperation';
import { SCHEDULE_VIEW_TYPES } from 'pages/ScheduleV6/constants/constants';
import { getEquipmentColorClass } from 'helpers/_helpers';

const ResourceConflictModal = ({ classes, data, errorData, closeModal }) => {
  const dispatch = useDispatch();

  const personalProfile = useSelector((store) => store.personalProfile.user);
  const currentLocation = useSelector((store) => store.personalProfile.organizationLocation);
  const { scheduleView } = useSelector((store) => store.schedule);
  const { workers, equipment } = useSelector((store) => store.schedule.resources);
  
  const { handleSubmit, control } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: { conflictAction: 'move' },
  });

  const [infoModal, setInfoModal] = useState({ isOpen: false, text: '' });
  const openInfoModal = (text) => setInfoModal({ isOpen: true, text });
  const closeInfoModal = () => setInfoModal({ isOpen: false, text: '' });
  
  const [overlappingModal, setOverlappingModal] = useState(null);
  const closeOverlappingModal = () => {
    setOverlappingModal(null);
    closeModal();
  };

  const onSaveOverlapping = () => {
    closeOverlappingModal();
  };

  const resourceData = useMemo(() => {
    if (data.removeData.resourceType === 'workers') return workers.find(w => w._id === data.removeData.resourceId);
    return equipment.find(e => e._id === data.removeData.resourceId);
  }, [workers, equipment, data.removeData])


  const onSubmit = async (values) => {
    const sourceSheetId = values.conflictAction === 'move' ? data.from || undefined : undefined;
    const resource = {
      _id: data.addData.resource._id,
      conflictAction: values.conflictAction,
      sourceSheetId: sourceSheetId,
    };

    if (data.addData.resourceType === 'workers') {
      try {
        const res = await $api.put(
          `${process.env.REACT_APP_BASE_URL}/sheets/${data.to}/workers`,
          { workers: [resource, ...data.sheet.workers] },
          { ...getOptions() }
        );

        dispatch(currentWorklogActions.setWorklogSheet(res.data));
        if (scheduleView === SCHEDULE_VIEW_TYPES.daily) dispatch(updateSheets());
        closeModal();
      } catch (e) {
        if (e.response?.data?.type === 'sheet-time-overlapse') {
          const isSameTenant = e.response.data.data?.every(entry => (
            entry.overlapseSheets?.every((s) => s.sheet.tenantLocationId === personalProfile.tenantLocationId)
          ))

          setOverlappingModal({
            reason: e.response.data.message,
            data: e.response.data.data,
            moveFrom: sourceSheetId,
            disableChangeTime: !isSameTenant,
          })
        }
        if (e.response?.data?.type === 'timeoff-time-overlapse') {
          openInfoModal(e.response.data.message);
        }
      }
    } else if (data.removeData.resourceType === 'equipment') {
      try {
        const res = await $api.put(
          `${process.env.REACT_APP_BASE_URL}/sheets/${data.to}/equipment`,
          { equipment: [resource, ...data.sheet.equipment] },
          getOptions()
        );

        dispatch(currentWorklogActions.setWorklogSheet(res.data));
        dispatch(updateSheets());
        closeModal();
      } catch (e) {
        if (e.response?.data?.type === 'sheet-time-overlapse') {
          setOverlappingModal({
            reason: e.response.data.message,
            data: e.response.data.data,
            moveFrom: sourceSheetId,
            type: e.response.data.resourceType,
          });
        }
        if (e.response?.data?.type === 'timeoff-time-overlapse') {
          openInfoModal(e.response.data.message);
        }
        if (e.response?.data?.type === 'calendar-event-time-overlapse') {
          openInfoModal(e.response.data.message);
        }
      }
    }
  };

  const headerText = useMemo(() => {
    if (
      errorData?.assignedToLocations?.length > 1 ||
      (errorData?.assignedToLocations?.[0] && errorData.assignedToLocations[0] !== currentLocation?.name)
    ) {
      const worklogsText = errorData.assignedToLocations.length > 1 ? 'worklogs' : 'a worklog';
      const branchesText = errorData.assignedToLocations.join(', ') + (errorData.assignedToLocations.length > 1 ? ' branches' : ' branch');
      return `Resource assigned to ${worklogsText} for the ${branchesText}`;
    }
    return 'Assigned resource(s)'
  }, [errorData, currentLocation]);
  
  if (!resourceData && data.removeData.resourceType !== 'equipment') return null;

  const workerShift = `${resourceData.shift || resourceData.profile?.shifts?.timeOfDay}`.toLowerCase();

  return (
    <>
      <Dialog
        open={true}
        onClose={closeModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          classes: {
            root: classes.dialogRoot,
          },
        }}
        disableEscapeKeyDown
      >
        <div className={classes.formWrapper} role="presentation">
          <PageLoader loading={false} small>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" className={classes.formFlex}>
              <div>
                <Typography variant="h4" style={{ marginBottom: '20px' }}>
                  {headerText}
                </Typography>

                <IconButton
                  className={classes.closeButton}
                  onClick={closeModal}
                  aria-label="Close"
                  disableTouchRipple
                >
                  <CloseIcon />
                </IconButton>

                <Grid container spacing={1} className={classes.flexBox}>
                  <Grid item xs={12} md={6}>
                    {data.removeData.resourceType === 'workers' ? (
                      <div className={classes.inlineFlex}>
                        <div
                          className={classnames(
                            classes.photoWrapper,
                            classes[`${workerShift}Border`]
                          )}
                        >
                          <img src={photoPlaceholder} className={classes.photo} />
                        </div>
                        <Typography
                          variant="body1"
                          color="textPrimary"
                          className={classnames(classes.name, classes[workerShift])}
                        >
                          {resourceData.name || resourceData?.username}
                        </Typography>
                      </div>
                    ) : (
                      <div
                        className={classnames(
                          classes.equipmentBlock,
                          getEquipmentColorClass(resourceData.color)
                        )}
                      >
                        <div className={classes.equipmentNumber} style={{ backgroundColor: resourceData.color }}>
                          {resourceData.number || parseInt(resourceData.name)}
                        </div>
                        <div className={classes.equipmentName} style={{ color: resourceData.color }}>
                          {resourceData.name}
                        </div>
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6} className={classes.textRight}>
                    <Controller
                      name="conflictAction"
                      control={control}
                      render={({ value, onChange }) => (
                        <RadioGroup
                          name="conflictAction"
                          value={value}
                          onChange={onChange}
                          className={classes.inlineFlex}
                        >
                          <FormControlLabel
                            value="move"
                            label="Move"
                            labelPlacement="end"
                            control={
                              <Radio
                                color="primary"
                                className={classes.radioButton}
                                checkedIcon={
                                  <span className={classnames(classes.icon, classes.checkedIcon)} />
                                }
                                icon={<span className={classes.icon} />}
                                disableRipple
                              />
                            }
                            className={classes.label}
                          />
                          <FormControlLabel
                            value="duplicate"
                            label="Duplicate"
                            labelPlacement="end"
                            control={
                              <Radio
                                color="primary"
                                className={classes.radioButton}
                                checkedIcon={
                                  <span className={classnames(classes.icon, classes.checkedIcon)} />
                                }
                                icon={<span className={classes.icon} />}
                                disableRipple
                              />
                            }
                            className={classes.label}
                          />
                        </RadioGroup>
                      )}
                    />
                  </Grid>
                </Grid>
              </div>

              <div className={classes.buttonHolder}>
                <Button
                  color="secondary"
                  variant="outlined"
                  className={classes.cancelButton}
                  onClick={closeModal}
                  disableRipple
                  disableTouchRipple
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="outlined"
                  className={classes.saveButton}
                  disableRipple
                  disableTouchRipple
                >
                  Save
                </Button>
              </div>
            </form>
          </PageLoader>
        </div>
      </Dialog>

      {infoModal.isOpen && (
        <AlertModal isOpen={infoModal.isOpen} info={infoModal.text} onClose={closeInfoModal} />
      )}
      {overlappingModal && (
        <OverlappingModal
          data={overlappingModal.data}
          message={overlappingModal.reason}
          onClose={closeOverlappingModal}
          onSave={onSaveOverlapping}
          moveFrom={overlappingModal.moveFrom}
          type={overlappingModal?.type}
          disableChangeTime={overlappingModal?.disableChangeTime}
        />
      )}
    </>
  );
};

ResourceConflictModal.propTypes = {
  classes: propTypes.object.isRequired,
  data: propTypes.object.isRequired,
  closeModal: propTypes.func.isRequired,
};

export default withStyles(styles)(ResourceConflictModal);
